import {
  Autocomplete,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../../features/userSlice";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import store from "../../../../../../store";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import {
  setAdGroupDataTrendsBreakdown,
  setCampaignDataTrendsBreakdown,
  setIsLoadingBDAds,
  setIsChildrenAPILoadingBDAds,
} from "../../../../../../features/trendsSlice";
import timeUnitParamMap from "../../helpers/funcs/timeUnitParamMap";
import { TrendsBreakdownQueryIds } from "../../helpers/funcs/trendsAllQueryIDs";
import { trendsService } from "../../../../../../services/api/trends.endpoints";
import TrendsSkeleton from "../../helpers/components/TrendsSkeleton";
import GraphSkeleton from "../../helpers/components/GraphSkeleton";
import widgetMetricMap, {
  funnelRatioMetricMap,
  profitAndFirstTimeDepositMetricMap,
} from "../../helpers/funcs/widgetMetricMap";
import {
  generateColorAdGroup,
  generateColorCampaign,
} from "../../helpers/funcs/trendsBDAdsHelpers";
import TrendsBDAdsCampaign from "./TrendsBDAdsCampaign";
import TrendsBDAdsAdgroup from "./TrendsBDAdsAdgroup";
import { getCommonParams } from "../../helpers/funcs/getCommonParams";

const TrendsBDAds = () => {
  const { userData } = useSelector((state: any) => state.user);
  const {
    campaignDataTrendsBreakdown,
    adGroupDataTrendsBreakdown,
    countryDataTrendsBreakdown,
    trafficSourceDataTrendsBreakdown,
    keywordMatchTypeDataTrendsBreakdown,
    brandsDataTrendsBreakdown,
    isLoadingBDAds,
    isChildrenAPILoadingBDAds,
  } = useSelector((state: any) => state.trends);

  const campaignData =
    campaignDataTrendsBreakdown?.data?.data?.query_output || [];
  const totalClicksCampaign = campaignData.reduce(
    (sum: any, item: any) => sum + (item.click_in || 0),
    0
  );
  const thresholdCampaign = totalClicksCampaign * 0.05;
  const filteredCampaigns = campaignData.filter(
    (item: any) => item.click_in !== null && item.click_in >= thresholdCampaign
  );
  const adGroupData =
    adGroupDataTrendsBreakdown?.data?.data?.query_output || [];
  const totalClicksAdGroup = adGroupData.reduce(
    (sum: any, item: any) => sum + (item.click_in || 0),
    0
  );
  const thresholdAdGroup = totalClicksAdGroup * 0.05;
  const filteredAdGroups = adGroupData.filter(
    (item: any) => item.click_in !== null && item.click_in >= thresholdAdGroup
  );
  const oauthTokenHeader: any = useSelector(selectUserToken);
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const [,] = useState(false);
  const pointDate = searchParams.get("point_date") || ""; // e.g., "22-09-2024"
  const fullWidgetName = searchParams.get("full_widget_name") || "";
  const metricNameFromUrl = searchParams.get("metric_name") || "";
  const valueSuffix = searchParams.get("value_suffix") || "";
  // const trafficSourceBDParam = searchParams.get("trafficSourceBD");
  const [day, month, year] = pointDate.split("-");
  const formattedDate = `${year}-${month}-${day}`;
  const [groupBy, setGroupBy] = useState("Campaign");
  const [trafficSource, setTrafficSource] = useState("g_ads_search");
  const [campaign, setCampaign] = useState("");
  const [campaignId, setCampaignId] = useState("");

  const [trafficSources, setTrafficSources] = useState<string[]>([]);
  const [campaigns, setCampaigns] = useState<{ id: string; name: string }[]>(
    []
  );
  const [campaignTrafficOptionsRaw, setCampaignTrafficOptionsRaw] = useState<
    any[]
  >([]);
  const [adGroupTrafficOptionsRaw, setAdGroupTrafficOptionsRaw] = useState<
    any[]
  >([]);

  const [campaignOptionsRaw, setCampaignOptionsRaw] = useState<any[]>([]);
  const getTimeUnit = () => {
    if (!fullWidgetName) return "day";
    const timeUnitKey = timeUnitParamMap[fullWidgetName] || "timeUnit";
    return searchParams.get(timeUnitKey) || "day";
  };
  const time_unit = getTimeUnit();

  useEffect(() => {
    let dataSource =
      groupBy === "Campaign"
        ? campaignTrafficOptionsRaw
        : adGroupTrafficOptionsRaw;

    const uniqueTrafficSources = [
      ...new Set(
        dataSource
          .filter((item: any) => item.traffic_source)
          .map((item: any) => item.traffic_source)
      ),
    ];
    setTrafficSources(uniqueTrafficSources);

    if (!trafficSource && uniqueTrafficSources.length > 0) {
      setTrafficSource(uniqueTrafficSources[0]);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy, campaignTrafficOptionsRaw, adGroupTrafficOptionsRaw]);

  useEffect(() => {
    let allCampaigns = new Map();

    // Get campaigns from adgroup data
    if (campaignOptionsRaw) {
      campaignOptionsRaw.forEach((item: any) => {
        if (item.campaign_id && item.campaign_name) {
          allCampaigns.set(item.campaign_id.toString(), {
            id: item.campaign_id.toString(),
            name: item.campaign_name,
          });
        }
      });
    }

    // Always update campaigns list
    const campaignList = Array.from(allCampaigns.values());
    setCampaigns(campaignList);

    // Handle default selection for Adgroup mode
    if (
      groupBy === "Adgroup" &&
      !campaign &&
      adGroupDataTrendsBreakdown?.data?.data?.query_output
    ) {
      const dataSource = adGroupDataTrendsBreakdown?.data?.data?.query_output;
      // Find default campaign with highest click_in total
      const campaignTotals = dataSource.reduce((totals: any, item: any) => {
        if (item.campaign_id == null) return totals;
        const campaignId = item.campaign_id.toString();
        const clickIn = item.click_in || 0;
        totals[campaignId] = (totals[campaignId] || 0) + clickIn;
        return totals;
      }, {});

      const defaultCampaignId = Object.keys(campaignTotals).reduce(
        (maxId, currentId) => {
          return campaignTotals[currentId] > (campaignTotals[maxId] || 0)
            ? currentId
            : maxId;
        },
        Object.keys(campaignTotals)[0]
      );

      const defaultCampaign = campaignList.find(
        (c: any) => c.id === defaultCampaignId
      );

      if (defaultCampaign) {
        setCampaign(defaultCampaign.name);
        setCampaignId(defaultCampaign.id);
      }
    } else if (groupBy === "Campaign") {
      // Clear campaign selection for Campaign mode
      setCampaign("");
      setCampaignId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    campaignDataTrendsBreakdown,
    adGroupDataTrendsBreakdown,
    groupBy,
    // Removed 'campaign' from dependencies to prevent infinite loop
  ]);

  const handleGroupByChange = async (value: string) => {
    setGroupBy(value);

    // Reset selections when switching modes
    setTrafficSource("");
    setCampaign("");
    setCampaignId("");

    // Trigger API call based on the selected mode with default values
    if (value === "Campaign") {
      // Use default traffic source from campaign data
      let defaultTrafficSource: any = "";
      if (campaignDataTrendsBreakdown?.data?.data?.query_output) {
        const uniqueTrafficSources = [
          ...new Set(
            campaignDataTrendsBreakdown.data.data.query_output
              .filter((item: any) => item.traffic_source)
              .map((item: any) => item.traffic_source)
          ),
        ];
        defaultTrafficSource = uniqueTrafficSources[0] || "g_ads_search";
      } else {
        defaultTrafficSource = "g_ads_search";
      }

      setTrafficSource(defaultTrafficSource);

      if (defaultTrafficSource) {
        fetchCampaignBreakdownData(defaultTrafficSource);
      }
    } else if (value === "Adgroup") {
      // Use default traffic source from adgroup data
      let defaultTrafficSource: any = "";
      let defaultCampaignId: any = "";

      if (adGroupDataTrendsBreakdown?.data?.data?.query_output) {
        const dataSource = adGroupDataTrendsBreakdown.data.data.query_output;

        // Get default traffic source
        const uniqueTrafficSources = [
          ...new Set(
            dataSource
              .filter((item: any) => item.traffic_source)
              .map((item: any) => item.traffic_source)
          ),
        ];
        defaultTrafficSource = uniqueTrafficSources[0] || "g_ads_search";

        // Get default campaign with highest click_in total
        const campaignTotals = dataSource.reduce((totals: any, item: any) => {
          if (item.campaign_id == null) return totals;
          const campaignId = item.campaign_id.toString();
          const clickIn = item.click_in || 0;
          totals[campaignId] = (totals[campaignId] || 0) + clickIn;
          return totals;
        }, {});

        defaultCampaignId = Object.keys(campaignTotals).reduce(
          (maxId, currentId) => {
            return campaignTotals[currentId] > (campaignTotals[maxId] || 0)
              ? currentId
              : maxId;
          },
          Object.keys(campaignTotals)[0]
        );

        const defaultCampaignName =
          dataSource.find(
            (item: any) => item.campaign_id.toString() === defaultCampaignId
          )?.campaign_name || "";

        setCampaign(defaultCampaignName);
        setCampaignId(defaultCampaignId);
      } else {
        defaultTrafficSource = "g_ads_search";
        defaultCampaignId = "1";
      }

      setTrafficSource(defaultTrafficSource);

      if (defaultTrafficSource && defaultCampaignId) {
        fetchAdGroupBreakdownData(defaultTrafficSource, defaultCampaignId);
      }
    }
  };

  const handleTrafficSourceChange = (newValue: string | null) => {
    const selectedSource = newValue || "";
    setTrafficSource(selectedSource);

    // Trigger API call
    if (selectedSource) {
      if (groupBy === "Campaign") {
        fetchCampaignBreakdownData(selectedSource);
      } else {
        fetchAdGroupBreakdownData(selectedSource, campaignId);
      }
    }
  };

  const handleCampaignChange = (newValue: string | null) => {
    const selectedCampaignName = newValue || "";
    setCampaign(selectedCampaignName);

    const selectedCampaign = campaigns.find(
      (c) => c.name === selectedCampaignName
    );
    const selectedCampaignId = selectedCampaign?.id || "";
    setCampaignId(selectedCampaignId);

    // Trigger API call for Adgroup mode
    if (groupBy === "Adgroup" && trafficSource && selectedCampaignId) {
      fetchAdGroupBreakdownData(trafficSource, selectedCampaignId);
    }
  };
  const fetchRawTrafficSources = async () => {
    try {
      const { type, website_shared_uid, dimensions } = await getCommonParams({
        searchParams,
        userData,
        dispatch,
        oauthTokenHeader,
        store,
      });

      const baseParams = {
        oauthTokenHeader,
        startDate: formattedDate,
        endDate: formattedDate,
        timeUnit: time_unit,
        type,
        websiteSharedUids: website_shared_uid,
        dimensions,
        campaignId: "1",
        adgroupId: "1",
        matchtype: "all",
        kwId: "1",
        country: "all",
        trafficSource: "all",
      };

      const [campaignRes, adGroupRes] = await Promise.all([
        trendsService.getTrendsBreakdownExecuteQuery({
          ...baseParams,
          queryId: TrendsBreakdownQueryIds.CAMPAIGN,
        }),
        trendsService.getTrendsBreakdownExecuteQuery({
          ...baseParams,
          queryId: TrendsBreakdownQueryIds.AD_GROUP,
        }),
      ]);

      setCampaignTrafficOptionsRaw(campaignRes?.data?.data?.query_output || []);
      setAdGroupTrafficOptionsRaw(adGroupRes?.data?.data?.query_output || []);
    } catch (e: any) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: e?.parsedText || e?.message || "Traffic source fetch failed",
          type: "error",
        })
      );
    }
  };

  const fetchRawInCampaign = async () => {
    try {
      const { type, website_shared_uid, dimensions } = await getCommonParams({
        searchParams,
        userData,
        dispatch,
        oauthTokenHeader,
        store,
      });
      const baseParams = {
        oauthTokenHeader,
        startDate: formattedDate,
        endDate: formattedDate,
        timeUnit: time_unit,
        type,
        websiteSharedUids: website_shared_uid,
        dimensions,
        campaignId: "1",
        adgroupId: "1",
        matchtype: "all",
        kwId: "1",
        country: "all",
        trafficSource: "all",
      };

      const response = await trendsService.getTrendsBreakdownExecuteQuery({
        ...baseParams,
        queryId: TrendsBreakdownQueryIds.AD_GROUP,
      });

      setCampaignOptionsRaw(response?.data?.data?.query_output || []);
    } catch (e: any) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: e?.parsedText || e?.message || "Traffic source fetch failed",
          type: "error",
        })
      );
    }
  };

  const fetchCampaignBreakdownData = async (trafficSourceParam: string) => {
    dispatch(setIsChildrenAPILoadingBDAds(true));
    try {
      const { type, website_shared_uid, dimensions } = await getCommonParams({
        searchParams,
        userData,
        dispatch,
        oauthTokenHeader,
        store,
      });

      const baseParams = {
        oauthTokenHeader,
        startDate: formattedDate,
        endDate: formattedDate,
        timeUnit: time_unit,
        type,
        websiteSharedUids: website_shared_uid,
        dimensions,
        campaignId: "1",
        adgroupId: "1",
        matchtype: "all",
        kwId: "1",
        country: "all",
        trafficSource: trafficSourceParam,
      };

      const response = await trendsService.getTrendsBreakdownExecuteQuery({
        ...baseParams,
        queryId: TrendsBreakdownQueryIds.CAMPAIGN,
      });

      dispatch(setCampaignDataTrendsBreakdown(response));
    } catch (e: any) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: e?.parsedText || e?.message || "Unknown error occurred",
          type: "error",
        })
      );
    } finally {
      dispatch(setIsChildrenAPILoadingBDAds(false));
    }
  };

  const fetchAdGroupBreakdownData = async (
    trafficSourceParam: string,
    campaignIdParam: string
  ) => {
    dispatch(setIsChildrenAPILoadingBDAds(true));
    try {
      const { type, website_shared_uid, dimensions } = await getCommonParams({
        searchParams,
        userData,
        dispatch,
        oauthTokenHeader,
        store,
      });

      const baseParams = {
        oauthTokenHeader,
        startDate: formattedDate,
        endDate: formattedDate,
        timeUnit: time_unit,
        type,
        websiteSharedUids: website_shared_uid,
        dimensions,
        campaignId: campaignIdParam || "1",
        adgroupId: "1",
        matchtype: "all",
        kwId: "1",
        country: "all",
        trafficSource: trafficSourceParam,
      };

      const response = await trendsService.getTrendsBreakdownExecuteQuery({
        ...baseParams,
        queryId: TrendsBreakdownQueryIds.AD_GROUP,
      });

      dispatch(setAdGroupDataTrendsBreakdown(response));
    } catch (e: any) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: e?.parsedText || e?.message || "Unknown error occurred",
          type: "error",
        })
      );
    } finally {
      dispatch(setIsChildrenAPILoadingBDAds(false));
    }
  };

  const fetchBreakdownAllAdsCampaignData = async () => {
    dispatch(setIsLoadingBDAds(true));
    try {
      const { type, website_shared_uid, dimensions } = await getCommonParams({
        searchParams,
        userData,
        dispatch,
        oauthTokenHeader,
        store,
      });

      const campaignId = searchParams.get("campaignId") || "1";
      const adgroupId = searchParams.get("adgroupId") || "1";
      const kwId = searchParams.get("kwId") || "1";
      const country = searchParams.get("country") || "all";

      const baseParams = {
        oauthTokenHeader,
        startDate: formattedDate,
        endDate: formattedDate,
        timeUnit: time_unit,
        type,
        websiteSharedUids: website_shared_uid,
        dimensions,
        campaignId,
        adgroupId,
        matchtype: "all",
        kwId,
        country,
        trafficSource: trafficSource,
      };

      const apiDefs = [
        {
          queryId: TrendsBreakdownQueryIds.CAMPAIGN,
          dispatchTo: setCampaignDataTrendsBreakdown,
        },
        {
          queryId: TrendsBreakdownQueryIds.AD_GROUP,
          dispatchTo: setAdGroupDataTrendsBreakdown,
        },
      ];

      const responses = await Promise.all(
        apiDefs.map(({ queryId }) =>
          trendsService.getTrendsBreakdownExecuteQuery({
            ...baseParams,
            // ...overrideParams,
            queryId,
          })
        )
      );

      responses.forEach((res: any, index) => {
        dispatch(apiDefs[index].dispatchTo(res));
      });
    } catch (e: any) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: e?.parsedText || e?.message || "Unknown error occurred",
          type: "error",
        })
      );
    } finally {
      dispatch(setIsLoadingBDAds(false));
    }
  };

  useEffect(() => {
    if (
      !countryDataTrendsBreakdown.status &&
      !trafficSourceDataTrendsBreakdown.status &&
      !keywordMatchTypeDataTrendsBreakdown.status &&
      !brandsDataTrendsBreakdown.status
    ) {
      return;
    }

    fetchBreakdownAllAdsCampaignData();
    fetchRawTrafficSources();
    fetchRawInCampaign();
    // eslint-disable-next-line
  }, [pointDate]);

  // Get unique countries with non-zero total CAC
  const metricKey =
    fullWidgetName === "Funnel Ratio"
      ? funnelRatioMetricMap[metricNameFromUrl] || ""
      : fullWidgetName === "Profit and First Time Deposit"
      ? profitAndFirstTimeDepositMetricMap[metricNameFromUrl] || ""
      : widgetMetricMap[fullWidgetName];
  const totalsCampaign: Record<string, number> = {};
  const groupedDataCampaign = (filteredCampaigns ?? []).reduce(
    (acc: any[], item: any) => {
      const timeUnit = item.time_unit;
      if (!timeUnit || isNaN(Date.parse(timeUnit))) return acc;

      const date = new Date(timeUnit);
      const formattedDate = format(date, "MMMM yyyy");

      const campaign = item.campaign_name;
      const rawValue = item[metricKey];
      const value = +(
        parseFloat(rawValue) * (valueSuffix === "%" ? 100 : 1)
      ).toFixed(2);
      if (!value || isNaN(value)) return acc;

      if (!totalsCampaign[campaign]) totalsCampaign[campaign] = 0;
      totalsCampaign[campaign] += value;

      let entry = acc.find((d) => d.x === formattedDate);
      if (!entry) {
        entry = { x: formattedDate };
        acc.push(entry);
      }

      entry[campaign] = (entry[campaign] || 0) + parseFloat(value.toFixed(2));
      entry[`click_in_${campaign}`] =
        (entry[`click_in_${campaign}`] || 0) + (item?.click_in || 0);
      return acc;
    },
    []
  );

  const uniqueSourcesCampaign = Object.entries(totalsCampaign)
    .filter(([source, total]) => source && !isNaN(total) && total !== 0)
    .sort((a, b) => b[1] - a[1]) // Descending sort
    .map(([source]) => source);

  const seriesCampaign = uniqueSourcesCampaign.map((source) => ({
    dataKey: source,
    color: generateColorCampaign(source),
    label: source.replace(/_/g, " "),
  }));

  const sortedDataCampaign = groupedDataCampaign.sort((a: any, b: any) => {
    const dateA = new Date(a.x + " 1");
    const dateB = new Date(b.x + " 1");
    return dateA.getTime() - dateB.getTime();
  });

  const totalsAdGroup: Record<string, number> = {};

  const groupedDataAdGroup = (filteredAdGroups ?? []).reduce(
    (acc: any[], item: any) => {
      const timeUnit = item.time_unit;
      if (!timeUnit || isNaN(Date.parse(timeUnit))) return acc;

      const date = new Date(timeUnit);
      const formattedDate = format(date, "MMMM yyyy");
      //Will be change to default
      const adgroup = item.adgroup_name;
      const rawValue = item[metricKey];
      const value = +(
        parseFloat(rawValue) * (valueSuffix === "%" ? 100 : 1)
      ).toFixed(2);
      if (!value || isNaN(value)) return acc;

      if (!totalsAdGroup[adgroup]) totalsAdGroup[adgroup] = 0;
      totalsAdGroup[adgroup] += value;

      let entry = acc.find((d) => d.x === formattedDate);
      if (!entry) {
        entry = { x: formattedDate };
        acc.push(entry);
      }

      entry[adgroup] = (entry[adgroup] || 0) + parseFloat(value.toFixed(2));
      entry[`click_in_${adgroup}`] =
        (entry[`click_in_${adgroup}`] || 0) + (item?.click_in || 0);
      return acc;
    },
    []
  );

  const uniqueSourcesAdGroup = Object.entries(totalsAdGroup)
    .filter(([source, total]) => source && !isNaN(total) && total !== 0)
    .sort((a, b) => b[1] - a[1]) // Descending sort
    .map(([source]) => source);

  const seriesAdGroup = uniqueSourcesAdGroup.map((source) => ({
    dataKey: source,
    color: generateColorAdGroup(source),
    label: source.replace(/_/g, " "),
  }));

  const sortedDataAdGroup = groupedDataAdGroup.sort((a: any, b: any) => {
    const dateA = new Date(a.x + " 1");
    const dateB = new Date(b.x + " 1");
    return dateA.getTime() - dateB.getTime();
  });

  return (
    <>
      {isLoadingBDAds ? (
        <TrendsSkeleton />
      ) : (
        <Paper sx={{ marginBottom: "16px", boxShadow: "none" }}>
          <Stack
            flexDirection={"row"}
            alignItems="center"
            sx={{ padding: "16px 24px" }}
            justifyContent={"space-between"}
          >
            <Box>
              <Typography variant="h6">Ads Breakdown</Typography>
            </Box>
            <Stack flexDirection={"row"}>
              <Stack flexDirection={"row"} alignItems={"center"} gap={1}>
                <FormLabel component="legend">By:</FormLabel>
                <RadioGroup
                  row
                  value={groupBy}
                  onChange={(e) => handleGroupByChange(e.target.value)}
                >
                  <FormControlLabel
                    value="Campaign"
                    control={<Radio color="secondary" />}
                    label="Campaign"
                    disabled={isChildrenAPILoadingBDAds}
                  />
                  <FormControlLabel
                    value="Adgroup"
                    control={<Radio color="secondary" />}
                    label="Adgroup"
                    disabled={isChildrenAPILoadingBDAds}
                  />
                </RadioGroup>
              </Stack>
              <Stack flexDirection={"row"} gap={2}>
                <Autocomplete
                  size="small"
                  disabled={isChildrenAPILoadingBDAds}
                  options={trafficSources}
                  value={trafficSource}
                  onChange={(_, newValue) =>
                    handleTrafficSourceChange(newValue)
                  }
                  sx={{ width: 180 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Traffic Source"
                      color="secondary"
                    />
                  )}
                />
                <Autocomplete
                  size="small"
                  options={campaigns.map((c) => c.name)}
                  value={campaign}
                  onChange={(_, newValue) => handleCampaignChange(newValue)}
                  disabled={groupBy === "Campaign" || isChildrenAPILoadingBDAds}
                  sx={{ width: 320 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="In Campaign"
                      color="secondary"
                    />
                  )}
                />
              </Stack>
            </Stack>
          </Stack>
          <Box
            sx={{
              backgroundColor: "#FFF4E5",
              padding: "8px 16px",
              borderRadius: "4px",
              border: "1px solid #ed6c02",
              marginX: "16px",
              width: "auto",
            }}
          >
            <Typography
              align="center"
              color="#ed6c02"
              display="flex"
              alignItems="center"
            >
              <ErrorOutlineOutlinedIcon style={{ marginRight: 5 }} />
              Only AdGroups that account for more than 5% of clicks in for the
              selected time unit are shown
              <Typography sx={{ marginLeft: 0.8 }}>
                {" "}
                to create new goals.
              </Typography>
            </Typography>
          </Box>
          {groupBy === "Campaign" ? (
            <Box>
              {isChildrenAPILoadingBDAds ? (
                <GraphSkeleton />
              ) : (
                <TrendsBDAdsCampaign
                  sortedDataCampaign={sortedDataCampaign}
                  seriesCampaign={seriesCampaign}
                />
              )}
            </Box>
          ) : (
            <Box>
              {isChildrenAPILoadingBDAds ? (
                <GraphSkeleton />
              ) : (
                <TrendsBDAdsAdgroup
                  sortedDataAdGroup={sortedDataAdGroup}
                  seriesAdGroup={seriesAdGroup}
                />
              )}
            </Box>
          )}
        </Paper>
      )}
    </>
  );
};

export default TrendsBDAds;
